import { useState } from "react";
import Screen from "../../components/Screen";
import { PrimaryBtn, GhostBtn } from "../../components/Buttons";
import Ico from "../../components/Ico";
import { Supplier } from "../../suppliers";
import { maskHouseNo, normalizeHouseNo, isValidHouseNo } from "@utils/func/houseNo";

interface NpConfirmSupplierProps {
  supplier: Supplier;
  /** 比對到的既有客戶遮罩電話（有值時顯示「已比對到您的資料」卡） */
  custPhone?: string;
  /** 比對到的既有客戶遮罩地址 */
  custAddr?: string;
  /** houseNo＝客戶在確認卡輸入的門牌號（已正規化），供上層送後端 verifyClaimedCis 驗證；沒有客戶卡時為 undefined。 */
  onYes: (houseNo?: string) => void;
  /** 不是這家 → 繼續問是否叫過桶裝瓦斯 */
  onNo: () => void;
  /** 「是這家」綁定既有客戶進行中：按鈕轉圈 + 禁點。 */
  busy?: boolean;
  /** 省略＝這一頁沒有上一步（流程第一頁）→ 不顯示返回鍵。由 Onboarding 的導航堆疊 canGoBack 決定。 */
  onBack?: () => void;
}

/** 傳單・無指定瓦斯行：手機/家用電話比對到 → 確認找到的瓦斯行是否正確，並一併確認比對到的客戶資料。 */
const NpConfirmSupplier = ({ supplier, custPhone, custAddr, onYes, onNo, busy, onBack }: NpConfirmSupplierProps) => {
  const hasCust = !!(custPhone || custAddr);
  const [houseNo, setHouseNo] = useState(""); // 確認卡上客戶輸入的門牌號（身分驗證用）
  // 門牌號驗證只在「有比對到客戶」時成立；沒客戶卡時這一頁純粹確認瓦斯行，不該多問門牌。
  const askHouseNo = hasCust;
  const houseNoOk = !askHouseNo || isValidHouseNo(houseNo);
  return (
    <Screen
      title="找到您所屬的瓦斯行"
      subtitle={hasCust ? (askHouseNo ? "請確認瓦斯行，並輸入門牌號碼確認是您本人" : "請確認瓦斯行與您的資料是否正確") : "請確認是不是這家瓦斯行"}
      onBack={onBack}
      footer={
        <>
          <PrimaryBtn active={houseNoOk} busy={busy} busyText="綁定中…" onClick={() => onYes(askHouseNo ? normalizeHouseNo(houseNo) : undefined)}>是，就是這家</PrimaryBtn>
          <GhostBtn onClick={onNo}>不是這家</GhostBtn>
        </>
      }
    >
      <div className="bg-[#F8FAFC] border border-[#EEF1F5] rounded-[16px] px-[18px] py-4">
        <div className="flex items-center gap-[14px]">
          <div className="w-[52px] h-[52px] rounded-[14px] bg-[#2196f3] flex items-center justify-center shrink-0">
            <Ico name="store" c="#fff" s={26} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[17px] font-extrabold text-[#1565C0]">{supplier.name}</div>
            <div className="text-xs text-[#94A3B8] mt-[3px]">合作瓦斯行</div>
          </div>
        </div>
        {(supplier.area || supplier.phone) && <div className="h-px bg-[#EEF1F5] my-[14px]" />}
        {(supplier.area || supplier.phone) && (
          <div className="flex flex-col gap-2.5">
            {supplier.area && (
              <div className="flex items-center gap-2 text-[13px]">
                {/* 後端帶回的是瓦斯行完整地址（supplier_address），比「服務區域」更能讓客戶辨識是不是這家 */}
                <span className="text-[#94A3B8] w-16 shrink-0">地址</span>
                <span className="font-semibold text-[#333]">{supplier.area}</span>
              </div>
            )}
            {supplier.phone && (
              <div className="flex items-center gap-2 text-[13px]">
                <span className="text-[#94A3B8] w-16 shrink-0">聯絡電話</span>
                <span className="font-semibold text-[#333]">{supplier.phone}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {hasCust && (
        <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-[14px] p-4 mt-4">
          <div className="flex items-center gap-2.5 mb-3.5">
            <div className="w-8 h-8 rounded-[9px] bg-[#10B981] flex items-center justify-center shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="text-[13px] font-extrabold text-[#15803D]">已比對到您的資料，這是您嗎？</div>
          </div>
          <div className="flex flex-col gap-3">
            {custPhone && (
              <div>
                <div className="text-[11.5px] text-[#64748B] font-semibold mb-0.5">電話</div>
                <div className="text-[15px] text-[#0f1a35] font-bold leading-[1.5]">{custPhone}</div>
              </div>
            )}
            {custAddr && (
              <div>
                <div className="text-[11.5px] text-[#64748B] font-semibold mb-0.5">地址</div>
                {/* 地址一律再遮一次：答案不能印在要客戶回答的卡片上 */}
                <div className="text-[15px] text-[#0f1a35] font-bold leading-[1.5]">{askHouseNo ? maskHouseNo(custAddr) : custAddr}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 門牌號輸入放在確認卡「外面」：卡片是系統給的資料，這裡是要客戶回答的問題，兩者不混在一起。 */}
      {askHouseNo && (
        <div className="mt-4">
          <div className="text-[13px] text-[#475569] font-semibold mb-2">請輸入上述地址的門牌號碼</div>
          <div className="flex items-center gap-2.5">
            <input
              value={houseNo}
              onChange={(e) => setHouseNo(e.target.value)}
              inputMode="numeric"
              placeholder="例：20"
              aria-label="門牌號碼"
              className="flex-1 h-[54px] px-4 border-none rounded-[14px] bg-[#F1F5F9] text-[17px] outline-none box-border text-[#333]"
            />
            <div className="text-[15px] font-bold text-[#0f1a35] shrink-0">號</div>
          </div>
          {!!houseNo && !isValidHouseNo(houseNo) && (
            <div className="mt-2 text-[12px] text-[#EF4444] font-semibold">請只填門牌號碼，例：20 或 20-1。</div>
          )}
        </div>
      )}
    </Screen>
  );
};

export default NpConfirmSupplier;
